import React, { useState } from "react";
import * as XLSX from "xlsx";

const UpgradApi = () => {
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  /* -------------------- SAMPLE EXCEL -------------------- */
  const downloadSampleExcel = () => {
    const sampleData = [
      {
        firstname: "Rahul",
        lastname: "Sharma",
        email: "rahul.sharma@example.com",
        phone: "9876543210",
        course: "MBA",
        city: "Mumbai",
        state: "Maharashtra",
        country: "India",
        utm_campaign: "summer_campaign"
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
    XLSX.writeFile(workbook, "upgrad_bulk_lead_sample.xlsx");
  };

  /* -------------------- API CALL -------------------- */
  const createLead = async (row) => {
    const headers = new Headers();
    headers.append("utm_source", "Leadsense");
    headers.append("utm_medium", "Leadsense");
    headers.append("utm_campaign", row.utm_campaign);
    headers.append("Content-Type", "application/json");
    headers.append(
      "Authorization",
      "Basic dWdjYnZlbmRvcjpraURwcVhtMQ=="
    );

    const body = {
      firstname: row.firstname,
      lastname: row.lastname,
      email: row.email,
      phone: { code: "+91", number: String(row.phone) },
      course: row.course,
      sendWelcomeMail: false,
      city: row.city,
      state: row.state,
      country: row.country,
      isDetectLocation: false,
      affiliateSource: "aff_id=1&sub_aff_id=12",
      leadSource: {
        platform: "DWeb",
        platformSection: "Apply Now",
        platformCounsellor: ""
      },
      emailTemplateSuffix: "in"
    };

    return fetch(
      "https://lead-management-service.upgrad.com/apis/lead-drop/vendor/ugcbvendor",
      {
        method: "POST",
        headers,
        body: JSON.stringify(body)
      }
    );
  };

  /* -------------------- FILE UPLOAD -------------------- */
  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setResults([]);
    setProgress(0);
    setLoading(true);

    const reader = new FileReader();
    reader.onload = async (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: "binary" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);

      setTotal(rows.length);

      for (let i = 0; i < rows.length; i++) {
        try {
          const res = await createLead(rows[i]);
          setResults((prev) => [
            ...prev,
            {
              email: rows[i].email,
              campaign: rows[i].utm_campaign,
              status: res.status === 201 ? "SUCCESS" : "FAILED",
              code: res.status
            }
          ]);
        } catch {
          setResults((prev) => [
            ...prev,
            {
              email: rows[i].email,
              campaign: rows[i].utm_campaign,
              status: "FAILED",
              code: "ERROR"
            }
          ]);
        }

        setProgress((p) => p + 1);
        await new Promise((r) => setTimeout(r, 300));
      }

      setLoading(false);
    };

    reader.readAsBinaryString(file);
  };

  const successCount = results.filter(r => r.status === "SUCCESS").length;
  const failCount = results.filter(r => r.status === "FAILED").length;

  /* -------------------- UI -------------------- */
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h2>Upgrad Bulk Lead Upload</h2>
        <p style={styles.subtitle}>
          Upload an Excel file to create leads in bulk
        </p>

        <div style={styles.actions}>
          <button style={styles.primaryBtn} onClick={downloadSampleExcel}>
            Download Sample Excel
          </button>

          <label style={styles.uploadBtn}>
            Upload Excel
            <input
              type="file"
              accept=".xlsx,.xls"
              hidden
              disabled={loading}
              onChange={handleUpload}
            />
          </label>
        </div>

        {loading && (
          <div style={styles.progressBox}>
            Uploading {progress} / {total}
            <div style={styles.progressTrack}>
              <div
                style={{
                  ...styles.progressFill,
                  width: `${(progress / total) * 100}%`
                }}
              />
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div style={styles.summary}>
            <span style={styles.successBadge}>
              Success: {successCount}
            </span>
            <span style={styles.failBadge}>
              Failed: {failCount}
            </span>
          </div>
        )}

        {results.length > 0 && (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>UTM Campaign</th>
                  <th>Status</th>
                  <th>HTTP Code</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={i}>
                    <td>{r.email}</td>
                    <td>{r.campaign}</td>
                    <td style={{
                      color: r.status === "SUCCESS" ? "#2e7d32" : "#c62828",
                      fontWeight: 600
                    }}>
                      {r.status}
                    </td>
                    <td>{r.code}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

/* -------------------- STYLES -------------------- */
const styles = {
  page: {
    background: "#f4f6f8",
    minHeight: "100vh",
    padding: 24,
    display: "flex",
    justifyContent: "center"
  },
  card: {
    background: "#fff",
    borderRadius: 12,
    padding: 28,
    maxWidth: 900,
    width: "100%",
    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
    fontFamily: "Segoe UI"
  },
  subtitle: {
    color: "#666",
    marginBottom: 20
  },
  actions: {
    display: "flex",
    gap: 12,
    marginBottom: 20
  },
  primaryBtn: {
    background: "#1976d2",
    color: "#fff",
    border: "none",
    padding: "10px 16px",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 600
  },
  uploadBtn: {
    background: "#4caf50",
    color: "#fff",
    padding: "10px 16px",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 600
  },
  progressBox: {
    marginBottom: 20
  },
  progressTrack: {
    height: 10,
    background: "#e0e0e0",
    borderRadius: 6,
    marginTop: 6
  },
  progressFill: {
    height: "100%",
    background: "#4caf50",
    borderRadius: 6,
    transition: "width 0.3s"
  },
  summary: {
    display: "flex",
    gap: 12,
    marginBottom: 16
  },
  successBadge: {
    background: "#e8f5e9",
    color: "#2e7d32",
    padding: "6px 12px",
    borderRadius: 20,
    fontWeight: 600
  },
  failBadge: {
    background: "#fdecea",
    color: "#c62828",
    padding: "6px 12px",
    borderRadius: 20,
    fontWeight: 600
  },
  tableWrapper: {
    overflowX: "auto"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse"
  }
};

export default UpgradApi;
